import { useState, useEffect } from 'react';
import { db } from './data/storage';
import { createDemoData } from './data/mock';
import { Header } from './components/Header';
import { FeedModal } from './components/FeedModal';
import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { WeightModal } from './components/WeightModal';
import { SettingsPage } from './components/SettingsPage';
import { AddAnimalModal } from './components/AddAnimalModal';
import { EditAnimalModal } from './components/EditAnimalModal';
import { AnimalDetailView } from './components/AnimalDetailView';
import { Onboarding } from './components/Onboarding';
import { useAnimals } from './hooks/UseAnimalsWithCache';
import { useSettings } from './hooks/useSettings';
import { useFeedings } from './hooks/UseFeedingsWithCache';
import { useWeights } from './hooks/UseWeightsWithCache';
import { SyncIndicator } from './components/Syncindicator';
import { formatTimeAgo, formatTimeUntil } from './utils/timeFormat';
import type { AnimalWithStats, Feeding, Animal, Weight } from './data/types';
import './App.css';
import React from 'react';

const ONBOARDING_KEY = 'fosterpal-onboarding-completed';


function App() {

    // Add this new useEffect for status bar
    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            StatusBar.setStyle({ style: Style.Light });
            StatusBar.setBackgroundColor({ color: '#FFF9F5' });
        }
    }, []);

    const [dbReady, setDbReady] = useState(false);
    const [showOnboarding, setShowOnboarding] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showAddAnimal, setShowAddAnimal] = useState(false);
    const [selectedAnimal, setSelectedAnimal] = useState<AnimalWithStats | null>(null);
    const [selectedAnimalForWeight, setSelectedAnimalForWeight] = useState<AnimalWithStats | null>(null);
    const [detailAnimal, setDetailAnimal] = useState<AnimalWithStats | null>(null);
    const [editAnimal, setEditAnimal] = useState<AnimalWithStats | null>(null);
    const [animalWeights, setAnimalWeights] = useState<Weight[]>([]);
    const { animals, loading, pendingSyncs, syncing, refresh: refreshAnimals, addAnimal, updateAnimal, deleteAnimal: removeAnimal } = useAnimals();
    const { settings, updateWeightUnit, formatWeight, gramsToOunces, ouncesToGrams } = useSettings();
    const { addFeeding } = useFeedings();
    const { addWeight } = useWeights();

    // Load weights for selected animal when weight modal opens
    useEffect(() => {
        const loadAnimalWeights = async () => {
            if (selectedAnimalForWeight) {
                const weights = await db.getWeights(selectedAnimalForWeight.id);
                setAnimalWeights(weights);
            } else {
                setAnimalWeights([]);
            }
        };
        loadAnimalWeights();
    }, [selectedAnimalForWeight]);

    useEffect(() => {
        const initializeApp = async () => {
            try {
                // Initialize database
                await db.init();

                // Check if we need to load demo data
                const existingAnimals = await db.getAnimals();
                const hasCompletedOnboarding = localStorage.getItem(ONBOARDING_KEY) === 'true';

                if (existingAnimals.length === 0) {
                    console.log('Loading sample data...');
                    const { animals, feedings, weights } = createDemoData();

                    // Add sample data
                    for (const animal of animals) {
                        await db.addAnimal(animal);
                    }
                    for (const feeding of feedings) {
                        await db.addFeeding(feeding);
                    }
                    for (const weight of weights) {
                        await db.addWeight(weight);
                    }

                    console.log('Sample data loaded!');

                    // Show onboarding if first time
                    if (!hasCompletedOnboarding) {
                        setShowOnboarding(true);
                    }
                }

                setDbReady(true);
            } catch (error) {
                console.error('Failed to initialize app:', error);
            }
        };

        initializeApp();
    }, []);

    const handleOnboardingComplete = () => {
        localStorage.setItem(ONBOARDING_KEY, 'true');
        setShowOnboarding(false);
    };

    const handleFeedSubmit = async (feeding: Feeding) => {
        await addFeeding(feeding);
        await refreshAnimals();
    };

    const handleAddAnimal = async (animal: Animal) => {
        await addAnimal(animal);
    };

    const handleWeightSubmit = async (weight: Weight) => {
        await addWeight(weight);
        await refreshAnimals();
    };

    const handleEdit = (animal: AnimalWithStats) => {
        setEditAnimal(animal);
    };

    const handleEditSubmit = async (animal: Animal) => {
        await updateAnimal(animal);
        // Update detailAnimal if it's the same animal being edited
        if (detailAnimal && detailAnimal.id === animal.id) {
            setDetailAnimal({ ...detailAnimal, ...animal });
        }
    };

    const handleArchive = async (animal: AnimalWithStats) => {
        const updatedAnimal = { ...animal, status: 'archived' as const, updatedAt: Date.now() };
        await updateAnimal(updatedAnimal);
    };

    const handleDelete = async (animal: AnimalWithStats) => {
        await removeAnimal(animal.id);
    };

    if (!dbReady || loading) {
        return (
            <div className="loading-screen">
                <div className="loading-cats">
                    <span className="cat cat-1">🐱</span>
                    <span className="cat cat-2">🐱</span>
                    <span className="cat cat-3">🐱</span>
                </div>
                <p>Herding cats...</p>
            </div>
        );
    }

    if (showOnboarding) {
        return <Onboarding onComplete={handleOnboardingComplete} />;
    }

    return (
        <>
            <Header onSettingsClick={() => setShowSettings(true)} />

            <SyncIndicator pendingSyncs={pendingSyncs} syncing={syncing} />

            <div className="container">
                {animals.length === 0 ? (
                    <div className="empty-state">
                        <div className="empty-state-icon">🐱</div>
                        <h2>No animals yet</h2>
                        <p>Tap the + button below to add your first foster</p>
                    </div>
                ) : (
                    <div className="animal-list">
                        {animals.map((animal, index) => (
                            <div
                                key={animal.id}
                                className="animal-card"
                                style={{ animationDelay: `${index * 0.1}s` }}
                                onClick={() => setDetailAnimal(animal)}
                            >
                                <div className="animal-header">
                                    <div className="animal-photo">🐱</div>
                                    <div className="animal-info">
                                        <h3>{animal.name}</h3>
                                        <p>{animal.notes || `Every ${animal.feedingIntervalHours} hours`}</p>
                                    </div>
                                    <div className="card-chevron">›</div>
                                </div>

                                <div className="animal-stats">
                                    <div className="stat">
                                        <div className="stat-label">Last Fed</div>
                                        <div className="stat-value">
                                            {animal.lastFeeding
                                                ? formatTimeAgo(animal.lastFeeding.timestamp)
                                                : 'Never'}
                                        </div>
                                    </div>
                                    <div className="stat">
                                        <div className="stat-label">Next Due</div>
                                        <div className={`stat-value ${animal.isOverdue ? 'overdue' : animal.isDue ? 'due' : 'ok'}`}>
                                            {animal.isOverdue ? '⚠️ Overdue!' :
                                                animal.isDue ? 'Due now!' :
                                                    animal.nextDueTime ? formatTimeUntil(animal.nextDueTime) : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="stat">
                                        <div className="stat-label">Last Weight</div>
                                        <div className="stat-value">
                                            {animal.lastWeight ? formatWeight(animal.lastWeight.weightGrams) : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="stat">
                                        <div className="stat-label">Amount</div>
                                        <div className="stat-value">
                                            {animal.lastFeeding ? `${animal.lastFeeding.amountMl} mL` : 'N/A'}
                                        </div>
                                    </div>
                                </div>

                                <button className="feed-btn" onClick={(e) => { e.stopPropagation(); setSelectedAnimal(animal); }}>
                                    🍼 Feed Now
                                </button>

                                <button className="weight-btn" onClick={(e) => { e.stopPropagation(); setSelectedAnimalForWeight(animal); }}>
                                    ⚖️ Log Weight
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <button className="fab" onClick={() => setShowAddAnimal(true)}>
                +
            </button>

            <SettingsPage
                isOpen={showSettings}
                onClose={() => setShowSettings(false)}
                weightUnit={settings.weightUnit}
                onWeightUnitChange={updateWeightUnit}
            />

            <AddAnimalModal
                isOpen={showAddAnimal}
                onClose={() => setShowAddAnimal(false)}
                onSubmit={handleAddAnimal}
            />

            <FeedModal
                animal={selectedAnimal}
                onClose={() => setSelectedAnimal(null)}
                onSubmit={handleFeedSubmit}
            />

            <WeightModal
                animal={selectedAnimalForWeight}
                onClose={() => setSelectedAnimalForWeight(null)}
                onSubmit={handleWeightSubmit}
                weightUnit={settings.weightUnit}
                formatWeight={formatWeight}
                gramsToOunces={gramsToOunces}
                ouncesToGrams={ouncesToGrams}
                animalWeights={animalWeights}
            />

            <AnimalDetailView
                animal={detailAnimal}
                onClose={() => setDetailAnimal(null)}
                onEdit={handleEdit}
                onArchive={handleArchive}
                onDelete={handleDelete}
                formatWeight={formatWeight}
            />

            <EditAnimalModal
                animal={editAnimal}
                isOpen={!!editAnimal}
                onClose={() => setEditAnimal(null)}
                onSubmit={handleEditSubmit}
            />
        </>
    );
}

export default App;